import { CommonModule } from '@angular/common'
import {
    Component,
    inject,
    OnInit,
    signal,
    ChangeDetectionStrategy,
} from '@angular/core'
import {
    ActivatedRoute,
    NavigationEnd,
    Router,
    RouterModule,
} from '@angular/router'
import { AuthStateService } from '@baboor/baboor-auth'
import { UserSettingsApiService } from '@baboor/baboor-user-setting'
import { TokenSharingService } from '@baboor/common-auth'
import { VersionUpdateNotificationModalComponent } from '@baboor/common-components'
import { PageLayout, PageLayoutService } from '@baboor/common-page-layouts'
import {
    AppDetailsService,
    BackButtonService,
    SeoService,
    StatusBarService,
    ThemeService,
} from '@baboor/common-services'
import { DisplayBlurComponent } from '@baboor/native-devices-util'
import { Capacitor } from '@capacitor/core'
import { TranslocoService } from '@jsverse/transloco'
import { ConfirmDialogModule } from '@openng/optimus-ui/confirmdialog'
import { ConfirmDialog } from '@openng/optimus-ui/confirmdialog'
import { ConfirmPopupModule } from '@openng/optimus-ui/confirmpopup'
import { ConfirmPopup } from '@openng/optimus-ui/confirmpopup'
import { DynamicDialogModule } from '@openng/optimus-ui/dynamicdialog'
import { DialogService } from '@openng/optimus-ui/dynamicdialog'
import { ToastModule } from '@openng/optimus-ui/toast'
import { Toast } from '@openng/optimus-ui/toast'
import { NgxSonnerToaster } from 'ngx-sonner'
import { filter, map } from 'rxjs'
import { environment } from '../environment/environment'
import { LayoutAdminComponent } from './main/layouts/components/layout-admin/layout-admin.component'
import { LayoutCenteredComponent } from './main/layouts/components/layout-centered/layout-centered.component'
import { LayoutClientDefaultComponent } from './main/layouts/components/layout-client-default/layout-client-default.component'
import { LayoutCtaComponent } from './main/layouts/components/layout-cta/layout-cta.component'
import { LayoutDefaultComponent } from './main/layouts/components/layout-default/layout-default.component'
import { LayoutEasyModeComponent } from './main/layouts/components/layout-easy-mode/layout-easy-mode.component'
import { LayoutPublicSecondaryComponent } from './main/layouts/components/layout-public-secondary/layout-public-secondary.component'
import { LayoutPublicComponent } from './main/layouts/components/layout-public/layout-public.component'
import { LayoutVendorDefaultComponent } from './main/layouts/components/layout-vendor-default/layout-vendor-default.component'

@Component({
    imports: [
        CommonModule,
        LayoutDefaultComponent,
        LayoutCenteredComponent,
        LayoutCtaComponent,
        LayoutPublicComponent,
        RouterModule,
        NgxSonnerToaster,
        ConfirmDialog,
        ConfirmPopup,
        Toast,
        LayoutPublicSecondaryComponent,
        DisplayBlurComponent,
        LayoutClientDefaultComponent,
        LayoutVendorDefaultComponent,
        LayoutAdminComponent,
        LayoutEasyModeComponent,
        ToastModule,
        ConfirmDialogModule,
        ConfirmPopupModule,
        DynamicDialogModule,
    ],
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrl: './app.component.scss',
    changeDetection: ChangeDetectionStrategy.Eager,
    providers: [AppDetailsService, DialogService],
})
export class AppComponent implements OnInit {
    readonly PageLayout = PageLayout
    private tokenSharingService = inject(TokenSharingService)
    private dialogService = inject(DialogService)
    private backButtonService = inject(BackButtonService)
    private statusBarService = inject(StatusBarService)
    private appDetailsService = inject(AppDetailsService)
    private authSateService = inject(AuthStateService)
    layoutService = inject(PageLayoutService)

    private seo = inject(SeoService)
    private router = inject(Router)
    private activatedRoute = inject(ActivatedRoute)
    private themeService = inject(ThemeService)
    private userSettingsApiService = inject(UserSettingsApiService)
    private remoteVersion = signal('')
    storedUrl = signal('')

    islocked = false

    async ngOnInit() {
        await this.backButtonService.initialize()
        await this.statusBarService.init()
        this.tokenSharingService.init()
        this.listenToRouterEvents()
        this.getAppVersionFromDb()
        if (this.authSateService.getUserId()) {
            this.loadUserTheme()
        }
    }

    private listenToRouterEvents() {
        this.router.events
            .pipe(
                filter((event) => event instanceof NavigationEnd),
                map(() => {
                    let route = this.activatedRoute
                    while (route.firstChild) {
                        route = route.firstChild
                    }
                    return route.snapshot.data
                }),
            )
            .subscribe((data) => {
                if (data['isPublicWebsite']) {
                    return
                }
                this.seo.updateMetaData({
                    title: data['title'] || 'Hair Style Club',
                    description: data['description'],
                    keywords: data['keywords'],
                    ogUrl: data['ogUrl'],
                    ogImage: data['ogImage'],
                    ogTitle: data['ogTitle'],
                    ogDescription: data['ogDescription'],
                })
            })
    }

    private loadUserTheme() {
        this.userSettingsApiService
            .getUsersSettingsByUserId()
            .subscribe((res) => {
                const settings = res.data?.settings
                if (settings) {
                    const isDark = settings['theme'] === 'dark'
                    this.themeService.setTheme(isDark)
                }
            })
    }

    async getAppVersionFromDb() {
        this.appDetailsService.getAppVersionFromDb().subscribe({
            next: ({ data }: any) => {
                this.remoteVersion.set(data.version)
                this.checkVersionMisMatch()
            },
        })
    }

    async checkVersionMisMatch() {
        const isNative = Capacitor.isNativePlatform()
        const currentVersion = (await this.appDetailsService.getAppDetails())
            .version
        this.prepareStoreRedirect()
        if (
            isNative &&
            !this.isUpToDate(currentVersion, this.remoteVersion())
        ) {
            this.dialogService.open(VersionUpdateNotificationModalComponent, {
                width: '30vw',
                modal: true,
                closable: false,
                breakpoints: { '960px': '75vw', '640px': '90vw' },
                data: this.storedUrl(),
            })
        }
    }

    isUpToDate(current: string, latest: string) {
        return current === latest
    }

    prepareStoreRedirect() {
        const platform = Capacitor.getPlatform()
        if (platform === 'android') {
            this.storedUrl.set(
                `https://play.google.com/store/apps/details?id=${environment.androidAppId}`,
            )
        } else if (platform === 'ios') {
            this.storedUrl.set(
                `https://apps.apple.com/app/${environment.iosAppId}`,
            )
        } else {
            this.storedUrl.set(
                `https://play.google.com/store/apps/details?id=${environment.androidAppId}`,
            )
        }
    }
}
